import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react'
import { useParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import { useRecoilState } from 'recoil'
import { currentPovState } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { ListRelatedPoints, RelatedPoint } from 'types/building'

import LazyThumbnail from 'components/atoms/LazyThumbnail'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'
import CloseIcon from 'components/icons/CloseIcon'

type Props = {
  selectedFile: string | number | undefined
  setSelectedFile: Dispatch<SetStateAction<string | number | undefined>>
  buildingId?: string | number
  initialPointId?: string // UUID of the panorama point to jump directly to
  fallbackData?: ListRelatedPoints
}

export default function ModalListRelatedPoints({
  selectedFile,
  setSelectedFile,
  buildingId,
  initialPointId,
  fallbackData
}: Props) {
  const { id: urlId } = useParams()
  const id = buildingId != null ? String(buildingId) : urlId
  const { navigate } = useDirectory()

  const [relatedPoints, setRelatedPoints] = useState<RelatedPoint[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10
  })

  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setCurrentPov] = useRecoilState(currentPovState)

  const { data: listRelatedPoints, isValidating } = useSWR<ListRelatedPoints>(
    selectedFile
      ? {
          url: `/v1/buildings/${id}/image-related-file/${selectedFile}`,
          params: { ...pagination }
        }
      : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  )

  // Use fallbackData when SWR has no data (before API is ready)
  const displayedPoints =
    relatedPoints.length > 0 ? relatedPoints : fallbackData?.edges ?? []

  // Use useEffect to control when to update relatedPoints
  useEffect(() => {
    if (listRelatedPoints?.edges) {
      if (pagination.page === 1) {
        // First page: replace the array
        setRelatedPoints(listRelatedPoints?.edges)
      } else {
        // Subsequent pages: append to the array, but avoid duplicates
        setRelatedPoints((prev) => {
          const existingIds = new Set(prev.map((item) => item.point.id))
          const newItems = listRelatedPoints?.edges.filter(
            (item) => !existingIds.has(item?.point?.id)
          )
          return [...prev, ...newItems]
        })
      }
    }
  }, [listRelatedPoints?.edges, pagination?.page])

  // Reset relatedPoints and pagination when selectedFile changes
  useEffect(() => {
    if (selectedFile) {
      setRelatedPoints([])
      setPagination({ page: 1, page_size: 10 })
    } else {
      // Clear data when modal is closed
      setRelatedPoints([])
      setPagination({ page: 1, page_size: 10 })
    }
  }, [selectedFile])
  const { ref: intersectingRef } = useIntersectionObserver({
    onIntersecting: () => {
      if (
        !isValidating &&
        listRelatedPoints &&
        relatedPoints?.length < listRelatedPoints?.pagination?.total
      ) {
        setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
      }
    }
  })

  const handleMoveToPoint = useCallback(
    (selectedFloor: number, selectedArea: number, selectedPoint: string) => {
      currentViewer?.selectedPoint !== selectedPoint && setCurrentPov(null)
      setCurrentViewer((prev) => ({
        ...prev,
        selectedFloor,
        selectedArea: `${selectedArea}`,
        selectedPoint
      }))
      navigate(`/viewer/buildings/${id}`)
    },
    [
      currentViewer?.selectedPoint,
      id,
      navigate,
      setCurrentPov,
      setCurrentViewer
    ]
  )

  // Auto-navigate to the specific point when data loads and initialPointId is set
  useEffect(() => {
    if (initialPointId && listRelatedPoints?.edges?.length) {
      const target = listRelatedPoints.edges.find(
        (e) => e.point?.id === initialPointId
      )
      if (target) {
        handleMoveToPoint(
          target.floorId ?? target.floor_id,
          target.point.areaId,
          target.point.id
        )
      }
    }
  }, [initialPointId, listRelatedPoints?.edges, handleMoveToPoint])

  const handleCloseModal = () => {
    setSelectedFile(undefined)
  }

  if (!selectedFile) return null

  return (
    <Modal open width={700} centered>
      <div
        className="absolute top-5 right-5 cursor-pointer"
        onClick={handleCloseModal}
      >
        <CloseIcon />
      </div>
      <Title title="フロア画像一覧" />
      <div className="min-h-[470px] max-h-[470px] space-y-[10px] overflow-auto">
        {displayedPoints.map((item) => (
          <div
            key={item?.point?.id}
            className="flex gap-5 items-center cursor-pointer"
            onClick={() =>
              handleMoveToPoint(
                item?.floorId ?? item?.floor_id,
                item?.point?.areaId,
                item?.point?.id
              )
            }
          >
            <div className="w-[220px] h-[110px] object-contain relative flex-shrink-0">
              <div className="absolute top-1 left-1 z-10 rounded-lg flex bg-[#021120] p-[2px] min-w-[16px]">
                <div className="m-auto  text-[13px] leading-[13px] font-bold text-white">
                  {item?.point?.order}
                </div>
              </div>
              <LazyThumbnail
                url={item.point.image360?.thumbnail || ''}
                name={item.point.image360?.thumbnail || ''}
                width={220}
                height={110}
              />
            </div>
            <div>
              <div className="flex font-medium items-start pb-2">
                <div className="flex-shrink-0">フロア：</div>
                <div>{item?.name}</div>
              </div>
              <div className="flex font-medium items-start">
                <div className="flex-shrink-0">エリア：</div>
                <div>{item?.point?.areaName}</div>
              </div>
            </div>
          </div>
        ))}
        {/* Spinner only shows when loading more pages (pagination), not when data is already available */}
        <Spinner spinning={isValidating && displayedPoints.length === 0}>
          <div
            id="intersecting boundary"
            className="h-10"
            ref={intersectingRef}
          />
        </Spinner>
      </div>
    </Modal>
  )
}

ModalListRelatedPoints.defaultProps = {
  buildingId: undefined,
  initialPointId: undefined,
  fallbackData: undefined
}
